import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Trash2, Plus, GripVertical, Loader2 } from 'lucide-react';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

/**
 * Sprint A2 — Create / edit an onboarding template.
 *
 * Position field is a hybrid dropdown (decision 4): pull from real
 * departments[].positions[], with "+ Add new" option for ad-hoc roles.
 * Each stage carries department, description, training type, hours,
 * and optional responsible user.
 */

const TRAINING_TYPES = [
  'interview',
  'workshop',
  'practical_training',
  'reading_assignment',
  'external_training',
  'certification',
  'examination',
];

const blankStage = (idx) => ({
  idx,
  name: '',
  department_id: null,
  description: '',
  training_type: 'interview',
  estimated_hours: 1,
  responsible_user_id: null,
  is_external: false,
});

export function OnboardingTemplateDialog({
  open, onOpenChange, template, departments = [], collaborators = [], onSaved,
}) {
  const { t } = useTranslation();
  const isEdit = !!template;
  const [position, setPosition] = useState('');
  const [customPosition, setCustomPosition] = useState('');
  const [usingCustom, setUsingCustom] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stages, setStages] = useState([blankStage(0)]);
  const [saving, setSaving] = useState(false);

  // Build the position dropdown from departments[].positions[]
  const positionOptions = useMemo(() => {
    const set = new Set();
    for (const d of departments) (d.positions || []).forEach((p) => set.add(p));
    return Array.from(set).sort();
  }, [departments]);

  useEffect(() => {
    if (open && template) {
      const pos = template.position || '';
      const inList = positionOptions.includes(pos);
      setPosition(inList ? pos : '__custom__');
      setUsingCustom(!inList);
      setCustomPosition(inList ? '' : pos);
      setName(template.name || '');
      setDescription(template.description || '');
      setStages((template.stages || []).map((s, i) => ({ ...blankStage(i), ...s, idx: i })));
    } else if (open) {
      setPosition('');
      setUsingCustom(false);
      setCustomPosition('');
      setName('');
      setDescription('');
      setStages([blankStage(0)]);
    }
  }, [open, template, positionOptions]);

  const addStage = () => setStages((s) => [...s, blankStage(s.length)]);
  const removeStage = (idx) =>
    setStages((s) => s.filter((x) => x.idx !== idx).map((x, i) => ({ ...x, idx: i })));
  const updateStage = (idx, patch) =>
    setStages((s) => s.map((x) => (x.idx === idx ? { ...x, ...patch } : x)));

  const resolvedPosition = usingCustom ? customPosition.trim() : position;

  const handleSave = async () => {
    if (!resolvedPosition) {
      toast.error(t('talentstart.template.errors.positionRequired', { defaultValue: 'Pick or enter a position.' }));
      return;
    }
    if (!name.trim()) {
      toast.error(t('talentstart.template.errors.nameRequired', { defaultValue: 'Template name is required.' }));
      return;
    }
    if (!stages.length || stages.some((s) => !s.name.trim())) {
      toast.error(t('talentstart.template.errors.stageRequired', { defaultValue: 'Every stage needs a name.' }));
      return;
    }
    setSaving(true);
    try {
      const body = {
        position: resolvedPosition,
        name: name.trim(),
        description: description.trim(),
        stages: stages.map((s, i) => ({
          idx: i,
          name: s.name.trim(),
          department_id: s.department_id || null,
          description: (s.description || '').trim(),
          training_type: s.training_type || 'interview',
          estimated_hours: Number(s.estimated_hours || 0),
          responsible_user_id: s.responsible_user_id || null,
          is_external: !!s.is_external,
        })),
      };
      const saved = isEdit
        ? await clientApi.onboardingPatchTemplate(template.id, body)
        : await clientApi.onboardingCreateTemplate(body);
      toast.success(
        isEdit
          ? t('talentstart.template.savedEdit', { defaultValue: 'Template updated' })
          : t('talentstart.template.savedNew', { defaultValue: 'Template created' }),
      );
      onSaved?.(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-[85vw] xl:max-w-[1280px] max-h-[88vh] overflow-y-auto" data-testid="onboarding-template-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('talentstart.template.editTitle', { defaultValue: 'Edit onboarding template' })
              : t('talentstart.template.newTitle', { defaultValue: 'New onboarding template' })}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{t('talentstart.template.position', { defaultValue: 'Position' })}</Label>
              <Select
                value={position}
                onValueChange={(v) => {
                  if (v === '__custom__') { setUsingCustom(true); setPosition('__custom__'); }
                  else { setUsingCustom(false); setPosition(v); }
                }}
              >
                <SelectTrigger data-testid="onboarding-template-position-select">
                  <SelectValue placeholder={t('talentstart.template.positionPlaceholder', { defaultValue: 'Pick a position…' })} />
                </SelectTrigger>
                <SelectContent>
                  {positionOptions.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">
                    + {t('talentstart.template.addCustom', { defaultValue: 'Add new role' })}
                  </SelectItem>
                </SelectContent>
              </Select>
              {usingCustom && (
                <Input
                  className="mt-2"
                  data-testid="onboarding-template-position-custom"
                  value={customPosition}
                  onChange={(e) => setCustomPosition(e.target.value)}
                  placeholder={t('talentstart.template.customPlaceholder', { defaultValue: 'Type the role name' })}
                />
              )}
            </div>
            <div>
              <Label>{t('talentstart.template.name', { defaultValue: 'Template name' })}</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="onboarding-template-name"
                placeholder={t('talentstart.template.namePlaceholder', { defaultValue: 'e.g. Onboarding — Administrative Director' })}
              />
            </div>
          </div>

          <div>
            <Label>{t('talentstart.template.description', { defaultValue: 'Description (optional)' })}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="onboarding-template-description"
              rows={2}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold">
                {t('talentstart.template.stages', { defaultValue: 'Route stages' })}
              </Label>
              <Button size="sm" variant="outline" onClick={addStage}
                      data-testid="onboarding-template-add-stage">
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t('talentstart.template.addStage', { defaultValue: 'Add stage' })}
              </Button>
            </div>
            <div className="space-y-3">
              {stages.map((s) => (
                <div key={s.idx} className="rounded-lg border p-3 bg-muted/30"
                     data-testid={`onboarding-template-stage-${s.idx}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs font-semibold text-muted-foreground">
                      #{s.idx + 1}
                    </span>
                    <Input
                      placeholder={t('talentstart.template.stageNamePlaceholder', { defaultValue: 'Stage name (e.g. Human Resources)' })}
                      value={s.name}
                      onChange={(e) => updateStage(s.idx, { name: e.target.value })}
                      data-testid={`onboarding-template-stage-${s.idx}-name`}
                    />
                    <Button size="icon" variant="ghost" onClick={() => removeStage(s.idx)}
                            data-testid={`onboarding-template-stage-${s.idx}-remove`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-4 gap-2">
                    <div>
                      <Label className="text-xs">{t('talentstart.template.department', { defaultValue: 'Department' })}</Label>
                      <Select
                        value={s.department_id || '__none__'}
                        onValueChange={(v) => updateStage(s.idx, { department_id: v === '__none__' ? null : v })}
                      >
                        <SelectTrigger data-testid={`onboarding-template-stage-${s.idx}-dept`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {departments.map((d) => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{t('talentstart.template.trainingType', { defaultValue: 'Training type' })}</Label>
                      <Select
                        value={s.training_type}
                        onValueChange={(v) => updateStage(s.idx, { training_type: v })}
                      >
                        <SelectTrigger data-testid={`onboarding-template-stage-${s.idx}-type`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TRAINING_TYPES.map((tt) => (
                            <SelectItem key={tt} value={tt}>
                              {t(`talentstart.trainingType.${tt}`, { defaultValue: tt.replace('_', ' ') })}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">{t('talentstart.template.estimatedHours', { defaultValue: 'Est. hours' })}</Label>
                      <Input
                        type="number" min="0" step="0.5"
                        value={s.estimated_hours}
                        onChange={(e) => updateStage(s.idx, { estimated_hours: e.target.value })}
                        data-testid={`onboarding-template-stage-${s.idx}-hours`}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('talentstart.template.responsible', { defaultValue: 'Responsible' })}</Label>
                      <Select
                        value={s.responsible_user_id || '__none__'}
                        onValueChange={(v) => updateStage(s.idx, { responsible_user_id: v === '__none__' ? null : v })}
                      >
                        <SelectTrigger data-testid={`onboarding-template-stage-${s.idx}-resp`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {collaborators.map((u) => (
                            <SelectItem key={u.id} value={u.id}>{u.name}{u.position ? ` — ${u.position}` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Textarea
                    className="mt-2"
                    rows={2}
                    placeholder={t('talentstart.template.stageDescription', { defaultValue: 'What happens in this stage…' })}
                    value={s.description}
                    onChange={(e) => updateStage(s.idx, { description: e.target.value })}
                    data-testid={`onboarding-template-stage-${s.idx}-desc`}
                  />
                  <div className="mt-2 flex items-center gap-2 text-xs">
                    <Switch
                      checked={!!s.is_external}
                      onCheckedChange={(v) => updateStage(s.idx, { is_external: v })}
                      data-testid={`onboarding-template-stage-${s.idx}-external`}
                    />
                    <span className="text-muted-foreground">
                      {t('talentstart.template.isExternal', { defaultValue: 'External (happens outside the company)' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="onboarding-template-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {t('common.save', { defaultValue: 'Save' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default OnboardingTemplateDialog;
